import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Table } from "antd";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Breadcrumbs from "../../../components/Breadcrumbs";
import SearchBox from "../../../components/SearchBox";
import AttachmentModal from "../../../components/modelpopup/AttachmentModal";
import DeleteModal from "../../../components/modelpopup/DeleteModal";
import EditReimbursementModal from "../../../components/modelpopup/EditReimbursementModal";
import CreateReimbursementModal from "../../../components/modelpopup/CreateReimbursementModal";
import ViewAttachmentModal from "../../../components/modelpopup/ViewAttachmentModal";
import { formatDateInTimezone } from "../../../utils/formatDateInTimezone";
import { decrypt } from "../../../utils/crypto";
import { companyID } from "../../../auth";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import config from "../../../config";

import {
  getAllExpenses,
  getApprovals,
  getNextLevelApprovals,
  deleteExpense,
  createExpense,
  updateExpense,
  updateReimbursementStatus,
} from "../../../services/reimbursementService";

const STATUS_MAP = {
  1: { label: "Pending", color: "rgb(255 223 0)" },
  2: { label: "Approved", color: "green" },
  3: { label: "Rejected", color: "red" },
};

const Reimbursement = () => {
  const { employee_id: encryptedId } = useParams();
  const employee_id = decrypt(encryptedId);

  const location = useLocation();
  const navigate = useNavigate();
  const { employeeName = "" } = location.state || {};

  const loginEmployeeId = localStorage.getItem("employeeID");
  const isOwner = String(loginEmployeeId) === String(employee_id);

  const [activeTab, setActiveTab] = useState("Applied Reimbursement");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [stats, setStats] = useState({
    applied: 0,
    pendingApproval: 0,
  });

  const [modalState, setModalState] = useState({
    show: false,
    attachment: null,
    status: 0,
    reimbursementId: null,
  });

  const [deleteModalState, setDeleteModalState] = useState({
    show: false,
    reimbursementId: null,
  });

  const [editModalState, setEditModalState] = useState({
    show: false,
    reimbursement: null,
  });

  const [viewAttachmentModal, setViewAttachmentModal] = useState({
    show: false,
    attachment: null,
  });

  const [createModalState, setCreateModalState] = useState({
    show: false,
  });

  const [acknowledge, setAcknowledge] = useState(false);

  // 🔹 Normalize data
  const normalize = useCallback((item) => {
    return {
      ...item,
      status: item.updated_status ?? item.expense_status ?? 1,
    };
  }, []);

  // 🔹 Load Applied
  const loadApplied = useCallback(async () => {
    try {
      const expenses = await getAllExpenses(employee_id);

      const filtered = expenses
        .filter((item) => item.status !== 0)
        .map(normalize);

      setStats((prev) => ({
        ...prev,
        applied: filtered.length,
      }));

      setData(filtered);
    } catch (error) {
      toast.error("Failed to load reimbursements");
    }
  }, [employee_id, normalize]);

  const loadPendingCount = useCallback(async () => {
    try {
      const [managerData, nextLevelData] = await Promise.all([
        getApprovals(employee_id),
        getNextLevelApprovals(employee_id),
      ]);

      const combinedRaw = [...managerData, ...nextLevelData];

      const uniqueMap = new Map();
      combinedRaw.forEach((item) => {
        uniqueMap.set(item.id, normalize(item));
      });

      const combined = Array.from(uniqueMap.values());
      const pendingCount = combined.filter((r) => r.status === 1).length;

      setStats((prev) => ({
        ...prev,
        pendingApproval: pendingCount,
      }));
    } catch (error) {
      console.error("Failed to load pending approval count:", error);
    }
  }, [employee_id, normalize]);

  // 🔹 Load Pending (Deduplicated)
  const loadPendingApproval = useCallback(async () => {
    try {
      const [managerData, nextLevelData] = await Promise.all([
        getApprovals(employee_id),
        getNextLevelApprovals(employee_id),
      ]);

      const combinedRaw = [...managerData, ...nextLevelData];

      // 🔥 Deduplicate by reimbursement ID
      const uniqueMap = new Map();
      combinedRaw.forEach((item) => {
        uniqueMap.set(item.id, normalize(item));
      });

      const combined = Array.from(uniqueMap.values());

      const pendingCount = combined.filter((r) => r.status === 1).length;

      setStats((prev) => ({
        ...prev,
        pendingApproval: pendingCount,
      }));

      setData(combined);
    } catch (error) {
      toast.error("Failed to load pending approvals");
    }
  }, [employee_id, normalize]);

  // 🔹 Main Loader
  const loadData = useCallback(
    async (tab) => {
      setLoading(true);
      setData([]); // 🔥 clear old data immediately
      setActiveTab(tab);

      try {
        if (tab === "Applied Reimbursement") {
          await loadApplied();
        } else {
          await loadPendingApproval();
        }
      } finally {
        setLoading(false);
      }
    },
    [loadApplied, loadPendingApproval]
  );

  useEffect(() => {
    loadData("Applied Reimbursement");
    loadPendingCount(); 
  }, [loadData, loadPendingCount]);

  const openDeleteModal = (id) => {
    setDeleteModalState({ show: true, reimbursementId: id });
  };

  const closeDeleteModal = () => {
    setDeleteModalState({ show: false, reimbursementId: null });
  };

  const confirmDelete = async () => {
    try {
      await deleteExpense(deleteModalState.reimbursementId);
      await loadData(activeTab);
      if (activeTab === "Applied Reimbursement") {
        await loadPendingCount(); 
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          "Failed to delete reimbursement";
      throw new Error(errorMessage);
    }
  };

  const openEditModal = (reimbursement) => {
    setEditModalState({ show: true, reimbursement });
  };

  const closeEditModal = () => {
    setEditModalState({ show: false, reimbursement: null });
  };

  const handleUpdateReimbursement = async (id, formData) => {
    try {
      await updateExpense(id, formData);
      await loadData(activeTab);
      if (activeTab === "Applied Reimbursement") {
        await loadPendingCount();
      }
    } catch (error) {
      console.error("Error updating reimbursement:", error);
      throw error;
    }
  };

  const openViewAttachmentModal = (attachment) => {
    setViewAttachmentModal({ show: true, attachment });
  };

  const closeViewAttachmentModal = () => {
    setViewAttachmentModal({ show: false, attachment: null });
  };

  const openCreateModal = () => {
    setCreateModalState({ show: true });
  };

  const closeCreateModal = () => {
    setCreateModalState({ show: false });
  };

  const handleCreateReimbursement = async (formData) => {
    try {
      await createExpense(formData);
      await loadData(activeTab);
      if (activeTab === "Applied Reimbursement") {
        await loadPendingCount();
      }
    } catch (error) {
      console.error("Error creating reimbursement:", error);
      throw error;
    }
  };

  const handleStatusUpdate = async (status_id, reimbursement_id) => {
    try {
      await updateReimbursementStatus(reimbursement_id, {
        approver_id: employee_id,
        status_id,
        company_id: companyID(),
      });

      toast.success(
        status_id === 2
          ? "Reimbursement approved successfully"
          : "Reimbursement rejected"
      );
      await loadData("Pending for Approval");
    } catch (error) {
      toast.error("Failed to update reimbursement status");
    }
  };


  console.log("data", data);
  const renderStatus = (status) => (
    <span style={{ color: STATUS_MAP[status]?.color }}>
      {STATUS_MAP[status]?.label}
    </span>
  );

  // 🔹 Table Columns
  const columns = useMemo(() => {
    const base = [
      {
        title: "Title",
        dataIndex: "expense_type",
      },
      {
        title: "Claimed On",
        dataIndex: "created_at",
        render: (_, record) =>
          formatDateInTimezone(record.created_at),
      },
      {
        title: "Expense Date",
        dataIndex: "expense_date",
        render: (_, record) =>
          formatDateInTimezone(record.expense_date),
      },
      {
        title: "Amount",
        dataIndex: "expense_amount",
      },
      {
        title: "Status",
        dataIndex: "status",
        render: renderStatus,
      },
    ];

    // 🔹 Employee column for Pending
    if (activeTab === "Pending for Approval") {
      base.unshift({
        title: "Employee",
        render: (_, record) =>
          `${record.first_name} ${record.middle_name ?? ""} ${record.last_name}`,
      });
    }

    if (activeTab === "Applied Reimbursement") {
      base.push({
        title: "Attachment",
        dataIndex: "attachment",
        render: (_, record) => (
          <div>
            {record.attachment ? (
              <button
                className="list-group-item list-group-item-action"
                onClick={() => openViewAttachmentModal(record.attachment)}
                style={{ cursor: "pointer" }}
              >
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>View Attachment</Tooltip>}
                >
                  <i className="fa-regular fa-eye text-info" style={{ fontSize: "16px" }} />
                </OverlayTrigger>
              </button>
            ) : (
              <span className="text-muted">-</span>
            )}
          </div>
        ),
      });
    }

    if (isOwner && activeTab === "Applied Reimbursement") {
      base.push({
        title: "Action",
        render: (_, record) => (
          <div className="d-flex gap-2">
            <button
              disabled={record.status !== 1}
              className="list-group-item list-group-item-action"
              onClick={() => openEditModal(record)}
              style={{
                cursor: record.status === 1 ? "pointer" : "not-allowed",
              }}
            >
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Edit</Tooltip>}
              >
                <i
                  className={`fa fa-pencil ${
                    record.status !== 1
                      ? "text-muted"
                      : "text-info"
                  }`}
                />
              </OverlayTrigger>
            </button>

            <button
              disabled={record.status !== 1}
              className="list-group-item list-group-item-action"
              onClick={() => openDeleteModal(record.id)}
              style={{
                cursor: record.status === 1 ? "pointer" : "not-allowed",
              }}
            >
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Delete</Tooltip>}
              >
                <i
                  className={`fa fa-trash ${
                    record.status !== 1
                      ? "text-muted"
                      : "text-danger"
                  }`}
                />
              </OverlayTrigger>
            </button>
          </div>
        ),
      });
    }

    // 🔹 Pending tab → View attachment (Always show)
    if (activeTab === "Pending for Approval") {
      base.push({
        title: "Action",
        render: (_, record) => (
          <button
            className="list-group-item list-group-item-action"
            onClick={() =>
              setModalState({
                show: true,
                attachment: record.attachment,
                status: record.status,
                reimbursementId: record.id,
              })
            }
            style={{ cursor: "pointer" }}
          >
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>View Attachment</Tooltip>}
            >
              <i className="fa-regular fa-eye text-info" style={{ fontSize: "16px" }} />
            </OverlayTrigger>
          </button>
        ),
      });
    }

    return base;
  }, [activeTab, isOwner, navigate]);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={`${employeeName} Reimbursement`}
          title="Dashboard"
          subtitle="Reimbursement"
        />

        {/* Stats */}
        <div className="row">
          {[
            { title: "Applied Reimbursement", value: stats.applied },
            { title: "Pending for Approval", value: stats.pendingApproval },
          ].map((item) => (
            <div className="col-md-3" key={item.title}>
              <div
                className="stats-info"
                onClick={() => loadData(item.title)}
                style={{
                  cursor: "pointer",
                  backgroundColor:
                    activeTab === item.title
                      ? "var(--company-theme)"
                      : "",
                }}
              >
                <h6
                  style={{
                    color:
                      activeTab === item.title
                        ? "white"
                        : "",
                  }}
                >
                  {item.title}
                </h6>
                <h4
                  style={{
                    color:
                      activeTab === item.title
                        ? "white"
                        : "",
                  }}
                >
                  {item.value}
                </h4>
              </div>
            </div>
          ))}
          {isOwner && (
            <div className="col-md-3 ms-auto d-flex align-items-center justify-content-end">
              <button
                className="btn btn-primary"
                onClick={openCreateModal}
                style={{ height: "fit-content" }}
              >
                <i className="fa fa-plus me-2" />
                Create Reimbursement
              </button>
            </div>
          )}
        </div>

        <SearchBox />

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: true }}
        />

        <AttachmentModal
          show={modalState.show}
          onHide={() =>
            setModalState((prev) => ({
              ...prev,
              show: false,
            }))
          }
          attachmentUrl={modalState.attachment}
          currentStatus={modalState.status}
          reimbursementId={modalState.reimbursementId}
          reimbursementStatusIdHandler={handleStatusUpdate}
          acknowledge={acknowledge}
          setAcknowledge={setAcknowledge}
          config={{ IMAGE_URL: config.IMAGE_URL }}
        />

        <DeleteModal
          show={deleteModalState.show}
          Name="Delete Reimbursement"
          confirmDelete={confirmDelete}
          onClose={closeDeleteModal}
        />

        <EditReimbursementModal
          show={editModalState.show}
          onHide={closeEditModal}
          reimbursement={editModalState.reimbursement}
          onUpdate={handleUpdateReimbursement}
        />

        <ViewAttachmentModal
          show={viewAttachmentModal.show}
          onHide={closeViewAttachmentModal}
          attachmentUrl={viewAttachmentModal.attachment}
          config={{ IMAGE_URL: config.IMAGE_URL }}
        />

        <CreateReimbursementModal
          show={createModalState.show}
          onHide={closeCreateModal}
          onCreate={handleCreateReimbursement}
          employeeId={employee_id}
        />
      </div>
    </div>
  );
};

export default Reimbursement;
